import {
  Box,
  Button,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Divider,
  Text,
  Flex,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
} from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { Link, useResolvedPath, useParams } from 'react-router-dom';

import { TapeBuilderUI } from '@finley-technologies/tape-builder';

import { Base64ConversionUtil } from './calcAgentQA/Base64ConversionUtil';
import { CalcAgentOperationPanel } from './calcAgentQA/CalcAgentOperationPanel';
import { ExportNodeButton } from './calcAgentQA/ExportNodeButton';
import { FileUploadPanel } from './calcAgentQA/FileUploadPanel';
import { ExecuteOperationPanel } from './calcAgentQA/OperationPanel';
import { useQAContext } from './calcAgentQA/QAContext';
import { TestExternalFilename } from './calcAgentQA/TestExternalFilename';
import { TextExternalReceivableIdRegEx } from './calcAgentQA/TextExternalReceivableIdRegEx';
import { TapePanelProps } from './commonTapeNode';
import { AutoConfigurePyCsvVirtualSheetPanel } from './configurationPanel/AutoConfigurePyCsvVirutalSheetPanel';
import { AutoConfigurePyXlsxVirtualSheet2CsvPanel } from './configurationPanel/AutoConfigurePyXlsxVirtualSheet2CsvPanel';
import { AutoConfigureSanitizePanel } from './configurationPanel/autoConfigureSanitizePanel';
import { AutoConfigureXlsx2CsvPanel } from './configurationPanel/autoConfigureXlsx2CsvPanel';
import { ConfigureParams } from './configurationPanel/types';
import { Xlsx2Csv2ConfigurationPanel } from './configurationPanel/Xlsx2Csv2ConfigurationPanel';
import { TapeBuilderMode } from './GraphContext';
import { JSONEditor } from './JSONEditor';
import {
  successToastConfig,
  errorToastConfig,
} from 'packages/common-frontend/constants';

type AutoConfigurePanelContainerProps = {
  tapeBuilderNode: TapeBuilderUI.TapeBuilderNodeAbstract;
  onConfigChange: (nodeId: string, newValue: object) => void;
};
const AutoConfigurePanelContainer = ({
  tapeBuilderNode,
  onConfigChange,
}: AutoConfigurePanelContainerProps) => {
  const { tapeBuilderNodeId, tapeBuilderNodeType } = tapeBuilderNode.metadata;

  const toast = useToast();

  const onAutoConfigure = useCallback(
    (params: ConfigureParams): void => {
      if (params.isValid) {
        const { configuration, operation } = params;
        onConfigChange(tapeBuilderNodeId, configuration);
        toast({
          ...successToastConfig,
          title: `Updated "${operation}" configuration. Please verify before saving.`,
        });
      } else {
        toast({
          ...errorToastConfig,
          title: `Detected ${params.errors.length} "${
            params.operation
          }" configuration error: ${params.errors.join(' | ')}`,
        });
      }
    },
    [onConfigChange, tapeBuilderNodeId, toast],
  );

  switch (tapeBuilderNodeType) {
    case 'operation_sanitize':
      return <AutoConfigureSanitizePanel onAutoConfigure={onAutoConfigure} />;
    case 'operation_xlsx2csv':
      return <AutoConfigureXlsx2CsvPanel onAutoConfigure={onAutoConfigure} />;
    case 'operation_xlsx2csv2':
      return (
        <Xlsx2Csv2ConfigurationPanel
          tapeBuilderNode={tapeBuilderNode}
          onAutoConfigure={onAutoConfigure}
        />
      );
    case 'operation_pyXlsxVirtualSheet2Csv':
      return (
        <AutoConfigurePyXlsxVirtualSheet2CsvPanel
          onAutoConfigure={onAutoConfigure}
          tapeBuilderNode={tapeBuilderNode}
        />
      );
    case 'operation_pyCsvVirtualSheet':
      return (
        <AutoConfigurePyCsvVirtualSheetPanel
          onAutoConfigure={onAutoConfigure}
          tapeBuilderNode={tapeBuilderNode}
        />
      );
    default:
      return null;
  }
};

type DeleteTapeBuilderNodeButtonProps = {
  nodeId: string;
  onDelete: (nodeId: string) => void;
};
const DeleteTapeBuilderNodeButton = ({
  nodeId,
  onDelete,
}: DeleteTapeBuilderNodeButtonProps) => {
  return (
    <Button
      onClick={() => onDelete(nodeId)}
      colorScheme="red"
      variant="outline"
      size="sm"
    >
      Delete Node
    </Button>
  );
};

type CommonNodePanelProps = {
  tapeBuilderNode: TapeBuilderUI.TapeBuilderNodeAbstract;
  schema: object;
  onConfigChange: (nodeId: string, newValue: object) => void;
  onDelete: (nodeId: string) => void;
  mode: TapeBuilderMode;
};
const CommonNodeConfigPanel = ({
  tapeBuilderNode,
  schema,
  onConfigChange,
  onDelete,
  mode,
}: CommonNodePanelProps) => {
  const nodeId = tapeBuilderNode.metadata.tapeBuilderNodeId;

  return (
    <JSONEditor
      contents={tapeBuilderNode.configuration}
      nodeId={nodeId}
      schema={schema}
      autoSave
      readOnly={mode !== 'Edit'}
      onSave={(id: string, newValue: object) => {
        onConfigChange(id, newValue);
      }}
    >
      {mode === 'Edit' && (
        <>
          {/* Auto-configuration section */}
          <AutoConfigurePanelContainer
            tapeBuilderNode={tapeBuilderNode}
            onConfigChange={onConfigChange}
          />

          {/* Separator for visual distinction */}
          <Divider my={2} />

          {/* Destructive actions section */}
          <Flex justify="flex-end">
            <DeleteTapeBuilderNodeButton nodeId={nodeId} onDelete={onDelete} />
          </Flex>
        </>
      )}
    </JSONEditor>
  );
};

type CommonTabPanelProps = TapePanelProps & { children: React.ReactNode };
const CommonTabPanel = (props: CommonTabPanelProps) => {
  const qaContext = useQAContext();
  const [tabIndex, setTabIndex] = useState(qaContext.state.qaMode ? 1 : 0);

  const { children, ...configPanelProps } = props;
  const { tapeBuilderNode } = props;

  const nodeName = `${TapeBuilderUI.getFriendlyNameForNode(
    tapeBuilderNode,
  )}: (${tapeBuilderNode.metadata.tapeBuilderNodeId})`;
  return (
    <Tabs
      index={tabIndex}
      onChange={(index) => setTabIndex(index)}
      variant="line"
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      colorScheme="black"
    >
      <Flex alignItems="center">
        <Text
          fontWeight="bold"
          fontSize="28px"
          mr="2"
          textTransform="capitalize"
        >
          {nodeName}
        </Text>
      </Flex>
      <TabList>
        <Tab>Configure</Tab>
        <Tab>QA</Tab>
      </TabList>
      <TabPanels flex={1} display="flex" flexDirection="column">
        <TabPanel flex={1} display="flex" flexDirection="column" p={0} pt={4}>
          <CommonNodeConfigPanel {...configPanelProps} />
        </TabPanel>
        <TabPanel flex={1} display="flex" flexDirection="column" p={0} pt={4}>
          {children}
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

// for generic operation nodes like sanitize, join, edit, flatten, etc.
export const OperationPanel = (props: TapePanelProps) => {
  return (
    <CommonTabPanel {...props}>
      <ExecuteOperationPanel tapeBuilderNode={props.tapeBuilderNode} />
    </CommonTabPanel>
  );
};

export const TestRawDocumentationConfig = (props: TapePanelProps) => {
  const { tapeBuilderNode } = props;

  if (
    TapeBuilderUI.tapeNodes.file_rawReceivableDocumentation.isNode(
      tapeBuilderNode,
    )
  ) {
    return (
      <>
        <TextExternalReceivableIdRegEx
          regEx={tapeBuilderNode.configuration.externalReceivableIdRegEx}
          decodeBase64={tapeBuilderNode.configuration.decodeBase64}
        />
        <Base64ConversionUtil />
      </>
    );
  }
  return null;
};

export const RawTapePanel = (props: TapePanelProps) => {
  const { tapeBuilderNode } = props;
  const externalFileName = TapeBuilderUI.isTapeBuilderRawTapeNode(
    tapeBuilderNode,
  )
    ? tapeBuilderNode.configuration.externalFilename
    : null;

  return (
    <CommonTabPanel {...props}>
      <FileUploadPanel tapeBuilderNode={props.tapeBuilderNode} />
      <TestRawDocumentationConfig {...props} />
      {externalFileName && (
        <TestExternalFilename externalFilename={externalFileName} />
      )}
    </CommonTabPanel>
  );
};

export const ConfigStoreLink = () => {
  const params = useParams();

  // we render data-pipeline upload drawer from multiple places.
  // 1. home page for borrowers/capital providers
  // 2. data-pipeline/ page for admin dashboard and customer dashboard
  // 3. data-pipelines/details page for admin dashboard and customer dashboard
  // the path to details page will be different from the admin dashboard vs customer dashboard.
  const destinationPath = useResolvedPath(
    `/organizations/${params.organizationId}/config-store`,
  );

  return (
    <Button
      size="lg"
      as={Link}
      to={destinationPath}
      variant="link"
      colorScheme="blue"
    >
      Config-Store
    </Button>
  );
};

export const ConfigTapePanel = (props: TapePanelProps) => {
  return (
    <>
      <Alert size="lg" status="error">
        <AlertIcon />
        <Flex direction="column" flexGrow={1}>
          <AlertTitle>
            These Settings have Moved To <ConfigStoreLink />{' '}
          </AlertTitle>
        </Flex>
      </Alert>

      <CommonTabPanel {...props}>
        <Box>{/* Admin panel removed - config moved to config store */}</Box>
      </CommonTabPanel>
    </>
  );
};

export const GeneratedTapePanel = (props: TapePanelProps) => {
  return (
    <CommonTabPanel {...props}>
      <Box>
        <FileUploadPanel tapeBuilderNode={props.tapeBuilderNode} />
        <Divider my="3" />
        <ExportNodeButton tapeBuilderNode={props.tapeBuilderNode} />
      </Box>
    </CommonTabPanel>
  );
};

export const ReceivableResponseTapeOperationPanel = (props: TapePanelProps) => {
  return (
    <CommonTabPanel {...props}>
      <CalcAgentOperationPanel
        tapeBuilderNode={props.tapeBuilderNode}
        orgSlugMap={props.orgSlugMap}
      />
    </CommonTabPanel>
  );
};
